/**
 * esetProtectionVocabulary — the one shared vocabulary for the ESET device-security observation's
 * `protection_status`, the field that says whether the vendor reports a device's protection as
 * active, at risk, or not reportable.
 *
 * `protection_status` is a CLOSED three-token set with no prose writer, declared on the adapter's
 * own result type (`eset_adapter.py`: `# "protected" | "at_risk" | "unknown"`). Both producers write
 * all three tokens, and the third one DOES reach a technician: the live adapter reports `unknown`
 * for a device whose `functionalityStatus` is absent, and calls that read `ok` when the record still
 * carried `deployedComponents` — which is what lets it past `_gather_eset_evidence`'s
 * `status != "ok"` gate and into `run.analysis["eset_observation"]`. (The mock's own `unknown`, a
 * `*-partial` device, sets `status="partial"` and is failed closed before any surface reads it; the
 * persisted shape is the live one.) So a technician meeting this field was reading the vendor's own
 * identifier — `unknown`, in English, inside a Hebrew card — and, on the run panel, in the same
 * green tone the card uses for `protected`.
 *
 * The threat list's `severity` and `action_taken` are the opposite case and are deliberately left
 * alone: `_map_threat` passes the vendor's own strings through lowercased, so those are evidence the
 * product does not own a vocabulary for, and echoing them is how the adapter's "preserve vendor
 * meaning" rule reaches the technician. This module words only the field the adapter derives itself.
 *
 * Three surfaces render this field and all three echoed the token, because each kept its own
 * short map with two arms and let the third fall through:
 *   - `AgentRunPanel`'s ESET card (`{... : obs.protection_status || "לא ידוע"}`);
 *   - `manager_agent._eset_grounded_explanation`, which builds the Hebrew `run.result_summary`
 *     sentence (`סטטוס הגנה: unknown.`) the technician reads on every surface that shows a run's
 *     own summary;
 *   - `conversation_service.evidence_sentence`, the plain operator sentence the employee's reply
 *     carries in the conversation (`לפי ESET מצב ההגנה הוא: unknown.`).
 *
 * The two backend sites cannot import this module, so the shared word for the third arm is held by
 * a parity pin instead of by a shared constant: `esetProtectionVocabulary.test.jsx` reads both
 * Python modules and asserts each names the third arm with the same Hebrew word this map carries.
 *
 * Why an unrecognised value is labelled rather than echoed, which is the opposite of the choice
 * `errorReasonVocabulary` makes for `technical_trace.error`. That field is a closed token set WITH a
 * prose escape hatch — two of its writers store a policy sentence or a run summary — so echoing an
 * unnamed value is how the operator keeps evidence the product did not word. This field has no prose
 * writer at all: every value it can hold is an identifier, so an unrecognised one carries nothing a
 * technician can read, and echoing it would put another English token in the Hebrew card this
 * iteration exists to clean. The fallback is therefore the `unknown` arm — the same treatment
 * `degradedAnalysisVocabulary` gives an arm it does not name, and `statusChipMeta` gives an
 * unrecognised status key. The cost is stated rather than hidden: a producer rename reads as
 * `לא ידוע` instead of as the new token, so the parity pin is the only control that sees the drift.
 *
 * The tone travels with the label for the same reason: `unknown` drawn in the `protected` green is a
 * claim about safety the observation does not support. Neutral is the colour this card already uses
 * for "no risk evidence", so no new palette is introduced.
 */

export const PROTECTION_STATUS_META = {
  protected: { label: "מוגן", cls: "text-green-700" },
  at_risk: { label: "בסיכון", cls: "text-red-700" },
  // The vendor returned no usable protection status. Named rather than echoed.
  unknown: { label: "לא ידוע", cls: "text-gray-600" },
};

/**
 * The label and tone for a `protection_status` value. A missing value and a value this build does
 * not name both read as the `unknown` arm — never as the raw token.
 */
export function protectionStatusMeta(value) {
  return PROTECTION_STATUS_META[value] || PROTECTION_STATUS_META.unknown;
}
